import React, { useState } from "react";
import { ReactSortable } from "react-sortablejs";

import "./PivotPreset.css";

import Input from "../../../components/FormComponents/Input";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import MultiSelectBox from "../../../components/FormComponents/MultiSelectBox";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import { generateObjectId } from "../../../utils/objectIdUtils";

const getPresetConfig = () => {
  return {
    aggregate_function: "",
    chart_type: "",
    cols: null,
    id: generateObjectId(),
    name: "New preset",
    preset_filters: [],
    rows: [],
    vals: []
  };
};

const CHART_TYPES = [
  { display: "Table" },
  { display: "Table Heatmap" },
  { display: "Table Col Heatmap" },
  { display: "Table Row Heatmap" },
  { display: "Exportable TSV" },
  { display: "Grouped Column Chart" },
  { display: "Stacked Column Chart" },
  { display: "Grouped Bar Chart" },
  { display: "Stacked Bar Chart" },
  { display: "Line Chart" },
  { display: "Dot Chart" },
  { display: "Area Chart" },
  { display: "Scatter Chart" },
  { display: "Multiple Pie Chart" }
];

const AGGREGATE_FUNCTIONS = [
  { display: "Count" },
  { display: "Count Unique Values" },
  { display: "List Unique Values" },
  { display: "Sum" },
  { display: "Integer Sum" },
  { display: "Average" },
  { display: "Median" },
  { display: "Sample Variance" },
  { display: "Sample Standard Deviation" },
  { display: "Minimum" },
  { display: "Maximum" },
  { display: "First" },
  { display: "Last" },
  { display: "Sum over Sum" },
  { display: "Sum as Fraction of Total" },
  { display: "Sum as Fraction of Rows" },
  { display: "Sum as Fraction of Columns" },
  { display: "Count as Fraction of Total" },
  { display: "Count as Fraction of Rows" },
  { display: "Count as Fraction of Columns" }
];

const EditPivotChart = ({ isDraggable, callback, selectedColumns, existingPresets }) => {
  const [presets, setPresets] = useState(existingPresets || []);

  const updateConfig = (config, type) => {
    callback(type, config);
    setPresets([...config]);
  };

  const onClickAddBtnHandler = () => {
    const allPresets = [...presets, getPresetConfig()];

    updateConfig(allPresets, "presets");
  };

  const presetHandler = (id, type, data) => {
    const indexOfTheFile = presets.findIndex((preset) => preset.id === id);

    presets[indexOfTheFile][type] = data;

    updateConfig(presets, "presets");
  };

  const multiSelectHandler = (id, type, options) => {
    const displayNameArray = options.map((option) => {
      return option.display;
    });

    presetHandler(id, type, displayNameArray);
  };

  const onDelete = (id) => {
    const filteredPresets = presets.filter((preset) => preset.id !== id);

    updateConfig(filteredPresets, "presets");
  };

  return (
    <div>
      <div className="tab_inside_container">
        <div className="mb_3">
          <h5 className="font_size_small mb_2">General Settings</h5>

          <div className="builder_checkbox_outer">
            <Input
              type="checkbox"
              name="search"
              checked={isDraggable || false}
              onChange={(e) => callback("is_draggable", e.target.checked)}
            />
            <label className="checkbox_label">Is Draggable</label>
          </div>
        </div>

        <div>
          <h5 className="font_size_small mb_1">Presets</h5>
          <div className="d_flex" style={{ width: "100%", justifyContent: "end", paddingBottom: "10px" }}>
            <button className="builder_button secondary" onClick={onClickAddBtnHandler}>
              Add
            </button>
          </div>

          <ReactSortable
            handle=".Recordsviewfielddragclass"
            list={presets}
            setList={(list) => {
              updateConfig(list);
            }}
            animation="200"
            easing="ease-out"
          >
            {presets.map((data) => (
              <div key={data.id} className="pivot_edit_preset builder_tiles field_card ">
                <div className="d_flex ">
                  <MaterialIcon
                    name="drag_indicator"
                    className="Recordsviewfielddragclass drag_icon font_size_xlarge cursor_grab"
                  />
                </div>

                <div className="tab_icon_container" style={{ top: "5px", right: "5px", position: "absolute" }}>
                  <ButtonActionIcon
                    className="icon_delete"
                    type="delete"
                    size="xsm"
                    onClick={() => onDelete(data.id)}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Title</label>
                  <Input
                    type="text"
                    value={data.name}
                    name="title"
                    placeholder="Enter Title of Preset"
                    onChange={(e) => presetHandler(data.id, "name", e.target.value)}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Chart Type</label>
                  <SingleSelectBox
                    selectedData={data?.chart_type}
                    valueKey="display"
                    labelKey={["display"]}
                    options={CHART_TYPES}
                    onChange={(option) => {
                      presetHandler(data.id, "chart_type", option.display);
                    }}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Rows</label>
                  <MultiSelectBox
                    onChange={(options) => multiSelectHandler(data.id, "rows", options)}
                    labelKey={["display"]}
                    valueKey="display"
                    options={selectedColumns}
                    selectedData={data?.rows}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Columns</label>
                  <MultiSelectBox
                    labelKey={["display"]}
                    valueKey="display"
                    options={selectedColumns}
                    onChange={(options) => multiSelectHandler(data.id, "cols", options)}
                    selectedData={data?.cols}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Aggregate function</label>
                  <SingleSelectBox
                    selectedData={data?.aggregate_function}
                    valueKey="display"
                    onChange={(option) => {
                      presetHandler(data.id, "aggregate_function", option.display);
                    }}
                    labelKey={["display"]}
                    options={AGGREGATE_FUNCTIONS}
                  />
                </div>

                <div className="builder_form">
                  <label className="builder_label">Aggregate Value</label>
                  <SingleSelectBox
                    selectedData={data?.vals[0]}
                    valueKey="display"
                    onChange={(option) => {
                      presetHandler(data.id, "vals", [option.display]);
                    }}
                    labelKey={["display"]}
                    options={selectedColumns}
                  />
                </div>
              </div>
            ))}
          </ReactSortable>
        </div>
      </div>
    </div>
  );
};

export default EditPivotChart;
